'use client'

import { Title, Text, Stack, ThemeIcon, Card, Grid, GridCol, Button, Center } from '@mantine/core'
import Section from '@/components/ui/Section'
import Link from 'next/link'
import { IconTrendingUp, IconUsers, IconHeart, IconBriefcase, IconStar, IconUserEdit } from '@tabler/icons-react'
import AIFeaturesSection from '@/components/AIFeaturesSection'
import MentorOperationsSection from '@/components/MentorOperationsSection'

export default function BecomeAMentor() {
  return (
    <>
      {/* Hero Section */}
      <Section bg="theme" py="xl" contentWidth="md">
        <Stack gap="xl" style={{ textAlign: 'center' }}>
          <Title order={1} size="h1" className="gradient-emerald">
            Become a Mentor
          </Title>
          <Text size="xl" c="navy.7" maw={550} mx="auto">
            Share what you know, help others grow, and build real connections along the&nbsp;way.
          </Text>
        </Stack>
      </Section>

      {/* The Process - 3 Steps */}
      <Section bg="white" py="xl" contentWidth="lg">
        <Stack gap="xl">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} size="h2" mb="md" className="gradient-emerald">
              How It Works
            </Title>
          </div>

          <Grid gutter="xl" mt="xl">
            <GridCol span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-pistachio-1)',
                  }}
                >
                  <IconUserEdit size={40} className="gradient-icon" />
                </Center>

                <Title order={3} size="h4" c="navy.8">
                  Create Your Profile
                </Title>

                <Text size="md" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7, textAlign: 'center' }}>
                  Apply with your professional background and expertise. Get approved to join our community of mentors.
                </Text>
              </Stack>
            </GridCol>

            <GridCol span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-pistachio-1)',
                  }}
                >
                  <IconUsers size={40} className="gradient-icon" />
                </Center>

                <Title order={3} size="h4" c="navy.8">
                  Connect with Mentees
                </Title>

                <Text size="md" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7, textAlign: 'center' }}>
                  Review requests from professionals seeking your guidance. Choose who you mentor and set your own schedule.
                </Text>
              </Stack>
            </GridCol>

            <GridCol span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-pistachio-1)',
                  }}
                >
                  <IconStar size={40} className="gradient-icon" />
                </Center>

                <Title order={3} size="h4" c="navy.8">
                  Make an Impact
                </Title>

                <Text size="md" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7, textAlign: 'center' }}>
                  Guide careers, share insights, and build meaningful relationships that last.
                </Text>
              </Stack>
            </GridCol>
          </Grid>
        </Stack>
      </Section>

      {/* Operations - Applications, Payments, Privacy */}
      <MentorOperationsSection />

      {/* Platform Features - Time-saving tools */}
      <AIFeaturesSection variant="mentor" />

      {/* Benefits Section */}
      <Section bg="grey" py="xl" contentWidth="lg">
        <Stack gap="xl">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} size="h2" mb="md" c="navy.8">
              Why Mentor on MentorSub?
            </Title>
            <Text size="lg" c="gray.7" maw={550} mx="auto">
              Mentoring isn&apos;t just about giving back - it&apos;s a chance to grow, learn, and expand your&nbsp;impact.
            </Text>
          </div>

          <Grid gutter="lg" mt="xl">
            <GridCol span={{ base: 12, sm: 6, md: 3 }}>
              <Card
                padding="lg"
                radius="md"
                h="100%"
                style={{
                  backgroundColor: 'white',
                  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Stack align="center" gap="sm" style={{ textAlign: 'center' }}>
                  <ThemeIcon size={60} radius="md" variant="light" color="emerald">
                    <IconHeart size={32} />
                  </ThemeIcon>
                  <Title order={3} size="h4" c="navy.8">
                    Give Back & Grow
                  </Title>
                  <Text size="md" c="gray.7">
                    Shape the next generation while developing your mentoring skills.
                  </Text>
                </Stack>
              </Card>
            </GridCol>

            <GridCol span={{ base: 12, sm: 6, md: 3 }}>
              <Card
                padding="lg"
                radius="md"
                h="100%"
                style={{
                  backgroundColor: 'white',
                  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Stack align="center" gap="sm" style={{ textAlign: 'center' }}>
                  <ThemeIcon size={60} radius="md" variant="light" color="emerald">
                    <IconUsers size={32} />
                  </ThemeIcon>
                  <Title order={3} size="h4" c="navy.8">
                    Expand Your Network
                  </Title>
                  <Text size="md" c="gray.7">
                    Connect with ambitious professionals and build meaningful relationships.
                  </Text>
                </Stack>
              </Card>
            </GridCol>

            <GridCol span={{ base: 12, sm: 6, md: 3 }}>
              <Card
                padding="lg"
                radius="md"
                h="100%"
                style={{
                  backgroundColor: 'white',
                  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Stack align="center" gap="sm" style={{ textAlign: 'center' }}>
                  <ThemeIcon size={60} radius="md" variant="light" color="emerald">
                    <IconBriefcase size={32} />
                  </ThemeIcon>
                  <Title order={3} size="h4" c="navy.8">
                    Build Your Reputation
                  </Title>
                  <Text size="md" c="gray.7">
                    Share your expertise and become known in your field.
                  </Text>
                </Stack>
              </Card>
            </GridCol>

            <GridCol span={{ base: 12, sm: 6, md: 3 }}>
              <Card
                padding="lg"
                radius="md"
                h="100%"
                style={{
                  backgroundColor: 'white',
                  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Stack align="center" gap="sm" style={{ textAlign: 'center' }}>
                  <ThemeIcon size={60} radius="md" variant="light" color="emerald">
                    <IconTrendingUp size={32} />
                  </ThemeIcon>
                  <Title order={3} size="h4" c="navy.8">
                    Flexible & Rewarding
                  </Title>
                  <Text size="md" c="gray.7">
                    Set your own schedule and gain fresh perspectives from your mentees.
                  </Text>
                </Stack>
              </Card>
            </GridCol>
          </Grid>
        </Stack>
      </Section>

      {/* CTA Section */}
      <Section bg="white" py="xl" contentWidth="md">
        <Stack gap="xl" align="center" style={{ textAlign: 'center' }}>
          <Title order={2} size="h2" c="navy.8">
            Ready to Make a Difference?
          </Title>
          <Text size="lg" c="gray.7" maw={550}>
            Join our community of mentors and help shape the next&nbsp;generation.
          </Text>
          <Link href="/mentoring/apply" style={{ textDecoration: 'none' }}>
            <Button size="xl" variant="gradient" gradient={{ from: 'emerald', to: 'teal', deg: 90 }}>
              Apply to Become a Mentor
            </Button>
          </Link>
        </Stack>
      </Section>
    </>
  )
}
